import cx from 'classnames';
import type { LottieRefCurrentProps } from 'lottie-react';
import Lottie from 'lottie-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import {
  useBoardId,
  useEnterpriseId,
  useWorkspaceId,
} from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { useGlobalTheme } from '@trello/theme';

import { LazySpotlightPulse } from 'app/src/components/Onboarding';
import { CarouselCard } from './CarouselCard';
import tacoPawIslandDarkModeLottie from './paw-island-dark-mode.json';
import tacoPawIslandLottie from './paw-island.json';

import styles from './CarouselScreen.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import arrowToInbox from 'resources/images/split-screen-onboarding/arrow-smaller.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import islandDecoration from 'resources/images/split-screen-onboarding/gestural-lines-island.png';
// eslint-disable-next-line @trello/assets-alongside-implementation
import trelloLogoLight from 'resources/images/split-screen-onboarding/trello-logo-light.svg';

interface CarouselScreenProps {
  onInboxNavigationClick: () => void;
  skipToFinalState: boolean;
  onAnimationEnd: () => void;
}

const estimatedLottieDurationFrames = 72;

export const CarouselScreen: FunctionComponent<CarouselScreenProps> = ({
  onInboxNavigationClick,
  skipToFinalState,
  onAnimationEnd,
}) => {
  const boardId = useBoardId();
  const workspaceId = useWorkspaceId();
  const enterpriseId = useEnterpriseId();
  const { effectiveColorMode } = useGlobalTheme();

  const tacoPawLottieFile =
    effectiveColorMode === 'dark'
      ? tacoPawIslandDarkModeLottie
      : tacoPawIslandLottie;

  const lottieRef = useRef<LottieRefCurrentProps>(null);

  const [selectedIndex, setSelectedIndex] = useState(skipToFinalState ? 4 : 0);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!skipToFinalState) {
        setSelectedIndex((prevState) => prevState + 1);
      }
    }, 2800);
    return () => clearInterval(timer);
  }, [setSelectedIndex, skipToFinalState]);

  const [lottieAnimationState, setLottieAnimationState] = useState<
    'done' | 'initial' | 'started'
  >('initial');

  const onLottieComplete = useCallback(() => {
    setLottieAnimationState('done');
  }, []);

  const onEndScreen = useMemo(
    () => skipToFinalState || lottieAnimationState === 'done',
    [skipToFinalState, lottieAnimationState],
  );

  useEffect(() => {
    if (skipToFinalState) {
      setSelectedIndex(4);
      if (selectedIndex > 3) {
        lottieRef.current?.goToAndStop(
          lottieRef.current?.getDuration(true) || estimatedLottieDurationFrames,
          true,
        );
      }
    }
  }, [selectedIndex, skipToFinalState]);

  useEffect(() => {
    // Skip animation when color mode changes.
    // We only want to set state to 'done' if the animation exists. Otherwise, we cannot start the animation
    // if the user changes color mode
    if (lottieRef.current) {
      setLottieAnimationState('done');
      lottieRef.current.goToAndStop(
        lottieRef.current.getDuration(true) || estimatedLottieDurationFrames,
        true,
      );
    }
  }, [effectiveColorMode]);

  useEffect(() => {
    if (onEndScreen) {
      Analytics.sendScreenEvent({
        name: 'splitScreenOnboardingEndScreen',
        containers: formatContainers({ boardId, workspaceId, enterpriseId }),
      });
      onAnimationEnd();
    } else {
      Analytics.sendScreenEvent({
        name: 'splitScreenOnboardingCarouselScreen',
        containers: formatContainers({ boardId, workspaceId, enterpriseId }),
      });
    }
  }, [onEndScreen, onAnimationEnd, boardId, workspaceId, enterpriseId]);

  useEffect(() => {
    if (selectedIndex > 2 && !skipToFinalState) {
      if (lottieAnimationState === 'initial') {
        setLottieAnimationState('started');
        lottieRef.current?.play();
      }
    }
  }, [
    setLottieAnimationState,
    selectedIndex,
    skipToFinalState,
    lottieAnimationState,
  ]);

  const onClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'openInboxOnboardingButton',
      source: 'splitScreenOnboardingEndScreen',
      containers: formatContainers({ boardId, workspaceId, enterpriseId }),
    });

    onInboxNavigationClick();
  };

  const inboxNavigationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { current } = inboxNavigationRef;
    if (current && (skipToFinalState || lottieAnimationState !== 'initial')) {
      current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [skipToFinalState, lottieAnimationState]);

  const stepClasses = [
    styles.firstStep,
    styles.secondStep,
    styles.thirdStep,
    styles.fourthStep,
  ];
  const currentStepClass =
    selectedIndex > 3 ? styles.endStep : stepClasses[selectedIndex];

  return (
    <div className={styles.carouselScreen}>
      <img src={trelloLogoLight} alt="Trello Logo" width={48} height={48} />
      <h1 className={styles.carouselScreenHeading}>
        <FormattedMessage
          id="templates.split_screen.onboarding.carousel-screen.title"
          defaultMessage="Meet the new Trello"
          description="Introducing to the new Trello"
        />
      </h1>
      <div
        className={cx(
          styles.animatingCards,
          selectedIndex === 0 ? styles.firstStep : styles.afterFirstStep,
        )}
      >
        <div
          className={cx(
            styles.inboxCard,
            currentStepClass,
            styles.animatingCard,
          )}
        >
          <CarouselCard cardType="inbox" />
        </div>
        <div
          className={cx(
            styles.plannerCard,
            currentStepClass,
            styles.animatingCard,
          )}
        >
          <CarouselCard cardType="planner" />
        </div>
        <div
          className={cx(
            styles.boardCard,
            currentStepClass,
            styles.animatingCard,
          )}
        >
          <CarouselCard cardType="board" />
        </div>
      </div>
      <div className={styles.progressIndicatorAndNavWrapper}>
        <div
          className={cx(
            styles.progressIndicator,
            !onEndScreen && styles.elementFadeOutInitial,
            lottieAnimationState === 'started' && styles.elementFadeOut,
            onEndScreen && styles.elementFadeOutFinal,
          )}
        >
          <div className={styles.progressIndicatorBackground}>
            <div className={styles.progressIndicatorValue}></div>
          </div>
        </div>
        {selectedIndex > 2 && (
          <div className={styles.island} ref={inboxNavigationRef}>
            {onEndScreen && (
              <div
                className={cx(
                  styles.islandDecorators,
                  lottieAnimationState === 'done' && styles.elementFadeIn,
                  skipToFinalState && styles.elementFadeInFinal,
                )}
              >
                <img
                  className={styles.arrow}
                  src={arrowToInbox}
                  alt="Arrow to inbox"
                />
                <img
                  className={styles.gesturalLines}
                  src={islandDecoration}
                  alt="Gestural lines"
                />
                <LazySpotlightPulse
                  className={styles.navigateToInboxPulse}
                  radius={8}
                >
                  <Button
                    appearance="subtle"
                    shouldFitContainer={true}
                    onClick={onClick}
                    className={styles.navigateToInboxButton}
                    aria-label="Navigate to inbox"
                  ></Button>
                </LazySpotlightPulse>
              </div>
            )}
            <Lottie
              lottieRef={lottieRef}
              animationData={tacoPawLottieFile}
              autoplay={false}
              loop={false}
              onComplete={onLottieComplete}
              className={styles.lottie}
            />
          </div>
        )}
      </div>
    </div>
  );
};
